import * as THREE from 'three';
import { CLASS_WEAPON, GEAR_BASES, type GearBaseId, type HeroClass } from '@somnhold/content';
import { DSTEEL, LEATHER_BELT, cloth, metal, spectral, std } from '../materials.ts';
import { G, M, V, glowSprite, limb, ring } from '../prims.ts';
import { aura } from './hero-tiers.ts';
import { CLOTH_A, CLOTH_B, CLOTH_TRIM, HIDE, HIDE_D, PLATE, gearHeld, type GearRarity } from './gear.ts';
import type { HeroRig, Model } from './types.ts';

/*
 * A hero dressed in what it wears (docs/EQUIPO.md §10, etapa 3). The class's builder draws its usual kit;
 * this swaps what is in its hands for the pieces it actually carries, before the model is baked (so it
 * costs nothing more per frame than the class's own model). The look comes from the sim's heroLook:
 * main ~ rarity ~ off hand ~ rarity ~ head ~ chest ~ feet, `-` for nothing worn and `u` for a unique piece (the
 * class's own model stands for those).
 */

export interface Look {
  main: GearBaseId | 'u' | null;
  rarity: GearRarity;
  off: GearBaseId | 'u' | null;
  offRarity: GearRarity;
  head: GearBaseId | 'u' | null;
  chest: GearBaseId | 'u' | null;
  feet: GearBaseId | 'u' | null;
  /** The titan's mark (level 50): its own glow. */
  titan: boolean;
}

const RARITIES: readonly string[] = ['common', 'uncommon', 'rare', 'veryRare', 'unique'];
const piece = (s: string | undefined): GearBaseId | 'u' | null => (!s || s === '-' ? null : s === 'u' ? 'u' : s in GEAR_BASES ? (s as GearBaseId) : null);

/** A look string (heroLook) read back; null when there is none. A piece in the wrong slot reads as nothing. */
export const parseLook = (s: string | undefined): Look | null => {
  if (!s) return null;
  const [main, rarity, off, offRarity, head, chest, feet, mark] = s.split('~');
  const rar = (r: string | undefined): GearRarity => (RARITIES.includes(r ?? '') ? r : 'common') as GearRarity;
  const worn = (x: string | undefined, slot: 'head' | 'armor' | 'boots'): GearBaseId | 'u' | null => {
    const p = piece(x);
    return p && p !== 'u' && GEAR_BASES[p].slot !== slot ? null : p;
  };
  return {
    main: piece(main), rarity: rar(rarity), off: piece(off), offRarity: rar(offRarity),
    head: worn(head, 'head'), chest: worn(chest, 'armor'), feet: worn(feet, 'boots'),
    titan: mark === 'T',
  };
};

/** How a piece sits in the hand (rotation of its pivot), by kind: upright staffs and bows, a levelled crossbow, tilted blades. */
const UPRIGHT: ReadonlySet<GearBaseId> = new Set(['staff', 'quarterstaff', 'longbow', 'shortbow']);
const heldPose = (base: GearBaseId, off: boolean): [number, number, number] => {
  if (base === 'heavyCrossbow') return [Math.PI / 2 - 0.15, 0, 0];
  if (UPRIGHT.has(base)) return [0.08, 0.2, 0];
  if (base === 'shield') return [0, -0.35, 0];
  if (GEAR_BASES[base].slot === 'offhand' && base !== 'offAxe') return [0.25, 0, 0];
  return off ? [0.35, 0, 0.12] : [0.35, 0, -0.12];
};

const clear = (g: THREE.Object3D): void => {
  for (const c of [...g.children]) g.remove(c);
};

/** Hangs a held piece on a hand pivot; a spectre's pieces turn spectral like the rest of it. */
const hold = (hand: THREE.Object3D, base: GearBaseId, rarity: GearRarity, off: boolean, ghost: string | undefined): void => {
  const p = gearHeld(base, rarity);
  p.rotation.set(...heldPose(base, off));
  if (base === 'shield') p.position.set(-0.06, 0.02, 0.02);
  // An orb rides above the hand, not down by the robe.
  if (base === 'orb') p.position.y = 0.14;
  if (ghost) {
    const mat = spectral(ghost);
    p.traverse((o) => {
      if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).material = mat;
    });
  }
  hand.add(p);
};

/**
 * Puts the look's pieces in the hero's hands: the main hand's piece (unless it is the class's usual weapon
 * at common, which the class draws better, or a unique piece), the off-hand's, and nothing in the other
 * hand with a two-handed weapon. With no off-hand piece the class keeps what it always held there (the
 * warrior's shield), so a hero who wears only an older weapon looks as it did.
 */
const dressHands = (rig: HeroRig, cls: HeroClass, look: Look, ghost: string | undefined): void => {
  // The ranger draws its bow with the left hand; its right hand nocks the arrow.
  const mainHand = cls === 'ranger' ? rig.offhand : rig.weapon;
  const offHand = cls === 'ranger' ? null : rig.offhand;
  const main = look.main;
  if (main && main !== 'u' && (main !== CLASS_WEAPON[cls] || look.rarity !== 'common')) {
    clear(mainHand);
    hold(mainHand, main, look.rarity, false, ghost);
  }
  const twoHanded = !!main && main !== 'u' && GEAR_BASES[main].hands === 2;
  if (!offHand) return;
  if (twoHanded) clear(offHand);
  else if (look.off && look.off !== 'u' && look.off !== 'quiver') {
    clear(offHand);
    hold(offHand, look.off, look.offRarity, true, ghost);
  }
};

/* ---------- head, chest and feet (docs/EQUIPO.md §2 «Peso», §10) ---------- */

/**
 * What a worn piece is made of, in figure scale. Every material is a shared, cached one (the cards' own,
 * models/gear.ts), so a dressed hero adds no material; a spectre's pieces are all its one spectral material.
 */
const TAU = Math.PI * 2;
const DARK = std('#0e0b0a');
const BROW = metal('#9aa0a8', { roughness: 0.3 });
const JEWEL = std('#9fb4c8', { roughness: 0.2 });
/** The warrior's crest red, for the plate helm's crest. */
const CREST = cloth('#7a2328');

/** Mail worn on the figure: darker and duller than plate, its grain standing for the rings. */
const MAIL = metal('#79808a', { roughness: 0.78, bumpScale: 0.08 });
const CLOTH_BOOT = std('#4a4458', { roughness: 0.95, bumpScale: 0.03 });
const CLOTH_WRAP = std('#2e2a36', { roughness: 1 });

/**
 * Hair for a head that only ever wore a hood or helm (the circlet shows it); the cleric and the barbarian
 * have their own. The mage's matches its beard.
 */
const HAIR: Partial<Record<HeroClass, THREE.Material>> = {
  warrior: std('#3b2a1f', { roughness: 0.9 }),
  artificer: std('#4a3424', { roughness: 0.9 }),
  mage: std('#c9c6bd', { roughness: 1 }),
  ranger: std('#4a3424', { roughness: 0.9 }),
  paladin: std('#2e2622', { roughness: 0.9 }),
  druid: std('#3a2e22', { roughness: 0.9 }),
};

/** The frame inside a pivot (figure coordinates follow the limb). */
const absOf = (pivot: THREE.Object3D): THREE.Object3D => pivot.children[0] ?? pivot;

const byName = (root: THREE.Object3D, name: string): THREE.Object3D[] => {
  const out: THREE.Object3D[] = [];
  root.traverse((o) => {
    if (o.name === name) out.push(o);
  });
  return out;
};

/** Adds a mesh in a shared material, or in the spectre's. */
type Put = (geo: THREE.BufferGeometry, mat: THREE.Material, parent: THREE.Object3D) => THREE.Mesh;
const putter = (ghost: string | undefined): { put: Put; mat: (m: THREE.Material) => THREE.Material } => {
  const g = ghost ? spectral(ghost) : null;
  return { put: (geo, mat, parent) => M(geo, g ?? mat, parent), mat: (m) => g ?? m };
};

/** A cowl with its face open toward +Z (hood and coif), in head frame. */
const cowl = (h: THREE.Object3D, put: Put, mat: THREE.Material, r: number, open: number): THREE.Mesh => {
  const c = put(new THREE.SphereGeometry(r, 10, 6, Math.PI / 2 + open, TAU - open * 2, 0, Math.PI * 0.8), mat, h);
  c.position.set(0, 1.645, -0.01);
  c.scale.set(1, 1.1, 1.06);
  return c;
};

const dressHead = (rig: HeroRig, cls: HeroClass, base: GearBaseId, ghost: string | undefined): void => {
  const h = absOf(rig.head);
  const { put, mat } = putter(ghost);
  for (const g of byName(h, 'headgear')) g.removeFromParent();
  if (base !== 'circlet') for (const k of byName(h, 'topknot')) k.removeFromParent();
  if (base === 'circlet') {
    const hair = HAIR[cls];
    if (hair) {
      const cap = put(new THREE.SphereGeometry(0.128, 9, 4, 0, TAU, 0, Math.PI / 2.1), hair, h);
      cap.position.set(0, 1.64, -0.012);
      cap.rotation.x = -0.32;
    }
    // A cloth band round the brow with a steel brow plate and a pale stone, two tails at the back.
    const band = G(h, 0, 1.685, 0);
    band.rotation.x = 0.12;
    ring(band, 0.127, 0.017, mat(CLOTH_B), 0, 3, 12);
    const plate = put(new THREE.OctahedronGeometry(0.04), BROW, band);
    plate.position.set(0, 0.012, 0.13);
    plate.scale.set(1.1, 1.3, 0.35);
    put(new THREE.OctahedronGeometry(0.019), JEWEL, band).position.set(0, 0.014, 0.143);
    for (const k of [-1, 1]) limb(band, V(k * 0.03, -0.005, -0.12), V(k * 0.07, -0.16, -0.16), 0.014, 0.008, mat(CLOTH_B), 4);
    return;
  }
  if (base === 'hood') {
    // Leather, the point falling back, a dark mantle round the throat.
    cowl(h, put, HIDE, 0.152, 0.78);
    const tip = put(new THREE.ConeGeometry(0.06, 0.2, 7), HIDE, h);
    tip.position.set(0, 1.74, -0.15);
    tip.rotation.x = -2.0;
    const m = put(new THREE.CylinderGeometry(0.13, 0.22, 0.1, 10, 1, true), HIDE, h);
    m.position.y = 1.5;
    m.scale.z = 0.9;
    return;
  }
  if (base === 'coif') {
    // Mail over the head and down onto the shoulders, ring rows across it, a leather edge at the hem.
    cowl(h, put, MAIL, 0.152, 0.6);
    const m = put(new THREE.CylinderGeometry(0.13, 0.23, 0.12, 10, 1, true), MAIL, h);
    m.position.y = 1.5;
    m.scale.z = 0.9;
    for (const [y, r] of [[1.7, 0.14], [1.47, 0.205]] as const) ring(h, r, 0.011, mat(DSTEEL), y, 3, 10).scale.y = 0.95;
    return;
  }
  // helm: a steel bascinet with cheek guards, a dark eye slit, a ridge on top and a rim at the neck.
  const dome = put(new THREE.SphereGeometry(0.148, 10, 4, 0, TAU, 0, Math.PI / 2), PLATE, h);
  dome.position.y = 1.655;
  dome.scale.set(1, 1.12, 1.07);
  const cheek = put(new THREE.CylinderGeometry(0.148, 0.14, 0.15, 10, 1, true), PLATE, h);
  cheek.position.y = 1.58;
  cheek.scale.z = 1.07;
  put(new THREE.BoxGeometry(0.2, 0.026, 0.05), DARK, h).position.set(0, 1.635, 0.14);
  put(new THREE.BoxGeometry(0.03, 0.13, 0.03), PLATE, h).position.set(0, 1.575, 0.158);
  // A short red crest along the top.
  const crest = put(new THREE.BoxGeometry(0.035, 0.085, 0.24), CREST, h);
  crest.position.set(0, 1.81, -0.02);
};

/** The class's chest pieces that a worn chest covers: its torso, shoulders and the layers worn over them. */
const chestParts = (b: THREE.Object3D): THREE.Mesh[] => {
  const torso = b.children.find((o) => o.name === 'torso') as THREE.Mesh | undefined;
  const skin = torso?.material;
  return b.children.filter((o): o is THREE.Mesh => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return false;
    if (m.name === 'torso' || m.name === 'shoulder') return true;
    const y = m.position.y;
    if (y < 1.0 || y > 1.46) return false;
    if (m.material === skin) return true;
    // A jerkin, capelet or mantle over the torso (a cylinder round it).
    const p = (m.geometry as THREE.CylinderGeometry).parameters;
    return m.geometry.type === 'CylinderGeometry' && Math.max(p.radiusTop, p.radiusBottom) >= 0.2;
  });
};

/** How far the class's chest reaches out at height y (its torso and the cylinders worn over it), for rings round it. */
const chestRadius = (parts: THREE.Mesh[], y: number): number => {
  let r = 0.16 + 0.05 * (y - 0.97) / 0.5;
  for (const m of parts) {
    if (m.geometry.type !== 'CylinderGeometry' || m.name === 'shoulder') continue;
    const p = (m.geometry as THREE.CylinderGeometry).parameters;
    if (p.thetaLength < TAU - 0.01) continue;
    const u = (y - m.position.y) / p.height + 0.5;
    if (u < 0 || u > 1) continue;
    r = Math.max(r, (p.radiusBottom + (p.radiusTop - p.radiusBottom) * u) * Math.max(m.scale.x, m.scale.z));
  }
  return r;
};

/**
 * A class's own long robe (mage, cleric, druid): the open cone or cylinder hanging from the hips to the ground,
 * the widest one when it wears two (the cleric's outer robe), and what is painted its colour (the cleric's folds).
 */
const longRobe = (fig: THREE.Object3D): THREE.Mesh[] => {
  let outer: THREE.Mesh | undefined;
  let widest = 0;
  for (const o of fig.children) {
    const m = o as THREE.Mesh;
    if (!m.isMesh || (m.geometry.type !== 'ConeGeometry' && m.geometry.type !== 'CylinderGeometry')) continue;
    const p = (m.geometry as THREE.CylinderGeometry).parameters;
    if (!p.openEnded || p.height < 0.8) continue;
    const r = m.geometry.type === 'ConeGeometry' ? (p as unknown as { radius: number }).radius : p.radiusBottom;
    if (r > widest) {
      widest = r;
      outer = m;
    }
  }
  if (!outer) return [];
  const hex = (outer.material as THREE.MeshStandardMaterial).color.getHex();
  return fig.children.filter((o): o is THREE.Mesh => {
    const m = o as THREE.Mesh;
    return !!m.isMesh && (m === outer || (m.material as THREE.MeshStandardMaterial).color?.getHex() === hex);
  });
};

/** A skirt hanging from the hips: in the figure, so it doesn't swing with the legs. */
const skirt = (fig: THREE.Object3D, put: Put, mat: THREE.Material, top: number, bottom: number, y0: number, y1: number): void => {
  put(new THREE.CylinderGeometry(top, bottom, y1 - y0, 12, 1, true), mat, fig).position.y = (y0 + y1) / 2;
};

const CHEST_MAT: Partial<Record<GearBaseId, THREE.Material>> = { robe: CLOTH_A, jerkin: HIDE, hauberk: MAIL, breastplate: PLATE };

const dressChest = (rig: HeroRig, base: GearBaseId, ghost: string | undefined): void => {
  const b = absOf(rig.body);
  const fig = rig.figure;
  const { put, mat } = putter(ghost);
  const cover = CHEST_MAT[base];
  const parts = chestParts(b);
  // A class already in a long robe (mage, cleric, druid) keeps its own colour under a cloth robe: grey would
  // wash out who it is. The sash and the collar say it is worn.
  const own = base === 'robe' ? longRobe(fig) : [];
  if (!ghost && cover && own.length === 0) for (const m of parts) m.material = cover;
  if (base === 'robe') {
    // The others get a tunic skirt to the knee; both add a red sash and a soft collar.
    if (own.length === 0) {
      skirt(fig, put, CLOTH_A, 0.175, 0.28, 0.46, 0.96);
      const end = put(new THREE.BoxGeometry(0.05, 0.3, 0.015), CLOTH_TRIM, fig);
      end.position.set(0.08, 0.83, 0.235);
      end.rotation.x = -0.25;
    }
    ring(b, 0.178, 0.026, mat(CLOTH_TRIM), 0.985, 3, 12);
    ring(b, 0.13, 0.035, mat(CLOTH_B), 1.47, 3, 12);
    return;
  }
  if (base === 'jerkin') {
    // Leather flaps over the hips, domed shoulder caps and a dark collar.
    for (let i = 0; i < 5; i++) {
      const a = (i - 2) * 0.62;
      const fl = G(fig, Math.sin(a) * 0.19, 0.84, Math.cos(a) * 0.19);
      fl.rotation.y = a;
      const p = put(new THREE.BoxGeometry(0.11, 0.2, 0.02), HIDE_D, fl);
      p.rotation.x = -0.18;
    }
    for (const k of [-1, 1]) {
      const cap = put(new THREE.SphereGeometry(0.1, 8, 3, 0, TAU, 0, Math.PI / 2), HIDE_D, b);
      cap.position.set(k * 0.235, 1.43, 0);
      cap.rotation.z = -k * 0.6;
      cap.scale.set(1.05, 0.6, 1.1);
    }
    ring(b, 0.125, 0.03, mat(HIDE_D), 1.47, 3, 12);
    return;
  }
  if (base === 'hauberk') {
    // A mail skirt below the belt with ring rows, and mail over the shoulders.
    skirt(fig, put, MAIL, 0.18, 0.26, 0.68, 0.96);
    for (const [y, r] of [[0.82, 0.225], [0.7, 0.257]] as const) ring(fig, r, 0.008, mat(DSTEEL), y, 3, 12);
    for (const y of [1.14, 1.3]) ring(b, chestRadius(parts, y) + 0.004, 0.008, mat(DSTEEL), y, 3, 12);
    const mantle = put(new THREE.CylinderGeometry(0.15, 0.25, 0.12, 10, 1, true), MAIL, b);
    mantle.position.y = 1.44;
    return;
  }
  // breastplate: plate pauldrons (in place of the class's own), a ridge and a fauld of three tassets.
  for (const g of [...b.children]) {
    if (!(g as THREE.Group).isGroup) continue;
    if (Math.abs(Math.abs(g.position.x) - 0.255) < 0.03 && Math.abs(g.position.y - 1.43) < 0.03) g.removeFromParent();
  }
  for (const k of [-1, 1]) {
    const pa = G(b, k * 0.25, 1.43, 0);
    pa.rotation.z = -k * 0.62;
    pa.scale.setScalar(1.2);
    put(new THREE.SphereGeometry(0.105, 8, 3, 0, TAU, 0, Math.PI / 2), PLATE, pa).scale.set(1.08, 0.5, 1.15);
    const lame = put(new THREE.SphereGeometry(0.1, 8, 1, 0, TAU, Math.PI / 2 - 0.5, 0.5), PLATE, pa);
    lame.position.y = -0.05;
    lame.scale.set(1.14, 0.9, 1.22);
  }
  for (const k of [-1, 0, 1]) {
    const ts = put(new THREE.BoxGeometry(0.12, 0.18, 0.024), PLATE, b);
    ts.position.set(k * 0.12, 0.83, 0.16 - Math.abs(k) * 0.04);
    ts.rotation.set(-0.15, k * 0.5, 0);
  }
};

const BOOT_MAT: Partial<Record<GearBaseId, THREE.Material>> = { clothBoots: CLOTH_BOOT, leatherBoots: HIDE, mailBoots: MAIL, greaves: PLATE };

const dressFeet = (rig: HeroRig, base: GearBaseId, ghost: string | undefined): void => {
  const { put, mat } = putter(ghost);
  const boot = BOOT_MAT[base] ?? HIDE;
  for (const shin of [rig.shinL, rig.shinR]) {
    if (!shin) continue;
    const s = absOf(shin);
    const k = shin === rig.shinL ? -1 : 1;
    for (const m of s.children) if (m.name === 'boot' && !ghost) (m as THREE.Mesh).material = boot;
    const x = k * 0.091;
    if (base === 'greaves') {
      // A plate greave over the shin, a ridge down its front and a knee cop.
      limb(s, V(x, 0.12, 0.005), V(k * 0.095, 0.46, 0.005), 0.074, 0.08, mat(PLATE), 6);
      put(new THREE.BoxGeometry(0.025, 0.3, 0.03), PLATE, s).position.set(x, 0.3, 0.075);
      const cop = put(new THREE.SphereGeometry(0.076, 6, 4), PLATE, s);
      cop.position.set(k * 0.095, 0.5, 0.045);
      cop.scale.set(1, 0.9, 0.75);
      continue;
    }
    // A boot shaft up the shin and its cuff.
    const top = base === 'clothBoots' ? 0.27 : 0.31;
    limb(s, V(x, 0.12, 0.005), V(x, top, 0.005), 0.071, 0.074, mat(boot), 6);
    const cuff = base === 'clothBoots' ? CLOTH_WRAP : base === 'leatherBoots' ? HIDE_D : LEATHER_BELT;
    ring(s, 0.077, 0.015, mat(cuff), top, 3, 8).position.set(x, top, 0.005);
    if (base === 'mailBoots') ring(s, 0.075, 0.007, mat(DSTEEL), 0.2, 3, 8).position.set(x, 0.2, 0.005);
    else if (base === 'clothBoots') ring(s, 0.074, 0.009, mat(CLOTH_WRAP), 0.18, 3, 8).position.set(x, 0.18, 0.005);
  }
};

/**
 * Dresses the hero in its look: the hands (dressHands), the head piece in place of the class's hat or helm,
 * the chest's weight on the torso and shoulders, and the boots. A slot with nothing worn, or a unique piece,
 * keeps the class's own model.
 */
export const dressHero = (m: Model, cls: HeroClass, look: Look, ghost?: string): void => {
  const rig = m.rig;
  if (!rig) return;
  dressHands(rig, cls, look, ghost);
  const worn = (p: GearBaseId | 'u' | null): GearBaseId | null => (p && p !== 'u' ? p : null);
  const head = worn(look.head), chest = worn(look.chest), feet = worn(look.feet);
  if (head) dressHead(rig, cls, head, ghost);
  if (chest) dressChest(rig, chest, ghost);
  if (feet) dressFeet(rig, feet, ghost);
  // The titan's mark (docs/EQUIPO.md §7): a dragon-red bloom round the body and a pale one at the heart (glow sprites, no light).
  if (look.titan && !ghost) {
    aura(rig.figure, '#ff4f3a');
    glowSprite(rig.figure, '#ffd2c8', 0.9, V(0, 1.25, 0.12), 0.22);
  }
};
